<template>
  <div>
    <div class="start-border" :style="{ display: 'none' }" />
    <div id="endBorder" class="end-border" :style="{ display: 'none' }" />
  </div>
</template>

<script>
import {
  BookSelection,
  SelectionContext
} from '@shared/publication/selection/SelectionUtils';
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('TextSelection.vue');

import isEqual from 'lodash/isEqual';
import CustomErrorEnum from '@shared/enums/CustomErrorEnum.mjs';
import SelectionEvents from '@/enums/SelectionEvents';
import TextSelectionTaskEnum from '@/enums/TextSelectionTaskEnum';
import SelectionDirectionEnum from '@shared/enums/SelectionDirectionEnum.mjs';
import FeatureDetectorService from '@/services/FeatureDetector/FeatureDetectorService';
import scrollUtils from '@/components/views/BookScroll/scrollUtils.js';

const longTouchDelayByPointerType = {
  mouse: 0,
  touch: 500,
  pen: 1300
};

const doubleTouchDelayByPointerType = {
  mouse: 0,
  touch: 200,
  pen: 1100
};

const START_SELECT_DELAY = 50;
const DOUBLE_CLICK_DELAY = 150;
const POINTER_DOWN_DELAY = 200;
let textSelectionTimer = 0;
let startSelectionTimer = null;

let preventTextClickSelection = false;

export default {
  name: 'TextSelection',
  props: {
    scrollSelector: String,
    elementSelector: String,
    shouldSelect: {
      type: Boolean,
      default: function() {
        return true;
      }
    },
    ignoredClassesForSelection: {
      type: Array,
      default: function() {
        return [];
      }
    },
    task: String
  },
  data() {
    return {
      selection: null,
      longtouchTimer: null,
      doubleTouchTimer: null,
      pointerDownTimer: null,
      shouldResetSelection: false,
      hasEventListeners: false,
      hasTouchEventListeners: false,
      hasPointerEventListeners: false,
      featureDetectorPromise: null,
      selectionContext: {},
      touchesCounter: null,
      animateScroll: null,
      animateScrollTime: 300
    };
  },
  computed: {
    isIpad() {
      return this.$store.getters['ContextStore/isIpad'];
    },
    scroll() {
      return this.$store.getters['PublicationStore/getAnimateScroll'];
    }
  },
  watch: {
    task(newTask) {
      this.processTask(newTask);
    }
  },
  async mounted() {
    this.featureDetectorPromise = FeatureDetectorService.getDetections();
    const { hasTouchEvents } = await this.featureDetectorPromise;
    this.selectionContext = new SelectionContext({
      hasTouchEvents,
      isIpad: this.isIpad
    });
    this.selection = new BookSelection(
      this.elementSelector,
      this.selectionContext
    );
    const element = this.$_getSelectionElement();
    if (element) {
      this.processTask(TextSelectionTaskEnum.INIT);
    }
  },
  destroyed() {
    const element = this.$_getSelectionElement();
    this.$_removeNotNeededListeners(element);
    this.featureDetectorPromise = null;
  },
  methods: {
    debStartSelection(event) {
      clearTimeout(startSelectionTimer);
      startSelectionTimer = setTimeout(() => {
        try {
          this.selection.startSelection(event);
        } catch (error) {
          logger.info(error);
        }
      }, START_SELECT_DELAY);
    },
    async processTask(task) {
      await this.featureDetectorPromise;
      if (!this.shouldSelect || !this.selection) {
        return;
      }
      const hasPointerInput = window.matchMedia('(pointer: fine)').matches;
      switch (task) {
        case TextSelectionTaskEnum.INIT:
          this.selection.reinitBorders();

          if (!this.selectionContext.hasTouchEvents) {
            this.$_addEventListeners();
          } else if ('PointerEvent' in window && this.isIpad) {
            this.$_addPointerEventListeners();
          } else {
            this.$_addTouchEventListeners();
            if (hasPointerInput) {
              this.$_addEventListeners();
            }
          }
          this.$_addCommonListeners();
          break;
        case TextSelectionTaskEnum.RESET_SELECTION:
          this.selection.resetSelection();
          break;
        default:
          break;
      }
    },
    $_getSelectionElement() {
      return document.querySelector(this.elementSelector);
    },
    $_addEventListeners() {
      const element = this.$_getSelectionElement();
      if (this.hasEventListeners || !element) {
        return;
      }
      element.addEventListener('mousedown', this.$_mouseDownHandler);
      document.addEventListener('mouseup', this.$_mouseUpHandler);
      element.addEventListener('mousemove', this.$_mouseMoveHandler);
      element.addEventListener('dblclick', this.$_dbClickHandler);
      document.addEventListener('wheel', this.$_wheelHandler, {
        passive: false
      });

      this.hasEventListeners = true;
    },
    $_removeEventListeners(element) {
      element.removeEventListener('mousedown', this.$_mouseDownHandler);
      document.removeEventListener('mouseup', this.$_mouseUpHandler);
      element.removeEventListener('mousemove', this.$_mouseMoveHandler);
      element.removeEventListener('dblclick', this.$_dbClickHandler);
      document.addEventListener('wheel', this.$_wheelHandler, {
        passive: false
      });
      this.hasEventListeners = false;
    },
    $_removeNotNeededListeners(element) {
      if (!element) {
        return;
      }
      if (this.selectionContext.hasTouchEvents) {
        this.$_removeTouchEventListeners(element);
        this.$_removePointerEventListeners(element);
      } else {
        this.$_removeEventListeners(element);
      }
      this.$_removeCommonListener();
    },
    $_addTouchEventListeners() {
      if (this.hasTouchEventListeners) {
        return;
      }
      const element = this.$_getSelectionElement();

      this.longtouchTimer = null;
      this.shouldResetSelection = false;
      element.addEventListener('touchstart', this.$_touchStartEventListener);
      document.addEventListener('touchend', this.$_touchEndEventListener);
      element.addEventListener('touchmove', this.$_touchMoveEventListener);
      this.hasTouchEventListeners = true;
    },
    $_removeTouchEventListeners(element) {
      element.removeEventListener('touchstart', this.$_touchStartEventListener);
      document.removeEventListener('touchend', this.$_touchEndEventListener);
      element.removeEventListener('touchmove', this.$_touchMoveEventListener);
      this.hasTouchEventListeners = false;
    },
    $_addPointerEventListeners() {
      if (this.hasPointerEventListeners) {
        return;
      }
      const element = this.$_getSelectionElement();
      this.longtouchTimer = null;
      this.shouldResetSelection = false;
      element.addEventListener('pointerdown', this.$_pointerDownEventListener);
      element.addEventListener('pointerup', this.$_pointerUpEventListener);
      document.addEventListener('pointermove', this.$_pointerMoveEventListener);
      this.hasPointerEventListeners = true;
    },
    $_removePointerEventListeners(element) {
      element.removeEventListener(
        'pointerdown',
        this.$_pointerDownEventListener
      );
      element.removeEventListener('pointerup', this.$_pointerUpEventListener);
      element.removeEventListener(
        'pointermove',
        this.$_pointerMoveEventListener
      );
      this.hasPointerEventListeners = false;
    },
    $_pointerDownEventListener(event) {
      switch (event.pointerType) {
        case 'touch':
          this.$_touchStartEventHandler(event, event.pointerType);
          break;
        case 'pen':
          /**
           * This is a workaround. After pointerdown event triggered,
           * pointermove event is not stopping to trigger.
           */
          clearTimeout(this.pointerDownTimer);
          this.pointerDownTimer = setTimeout(() => {}, POINTER_DOWN_DELAY);
          this.$_touchStartEventHandler(event, event.pointerType);
          break;
        default:
          break;
      }
    },
    $_pointerUpEventListener(event) {
      switch (event.pointerType) {
        case 'touch':
          this.$_touchEndEventHandler();
          break;
        case 'pen':
          this.$_touchEndEventHandler();
          break;
        default:
          break;
      }
    },
    $_pointerMoveEventListener(event) {
      switch (event.pointerType) {
        case 'touch':
          this.$_touchMoveEventHandler(event);
          break;
        case 'pen':
          if (this.pointerDownTimer && !this.selection.isActive) {
            return;
          }
          this.$_touchMoveEventHandler(event);
          break;
        default:
          break;
      }
    },
    $_resetTimer() {
      if (this.longtouchTimer) {
        clearTimeout(this.longtouchTimer);
        this.longtouchTimer = null;
      }
    },
    $_addCommonListeners() {
      const scrollElement = this.$_getScrollelement();
      if (scrollElement) {
        scrollElement.addEventListener('scroll', this.$_scrollHandler);
      }
    },
    $_removeCommonListener() {
      const scrollElement = this.$_getScrollelement();
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', this.$_scrollHandler);
      }
    },
    $_getScrollelement() {
      return document.querySelector(`.${this.scrollSelector}`);
    },
    $_touchStartEventListener(event) {
      this.$_touchStartEventHandler(event);
    },
    async $_touchStartEventHandler(event, pointerType = 'touch') {
      try {
        if (this.$_isClickedOnIgnoredClass(event)) {
          return false;
        }
        this.selection.setStartPosition(event);

        if (this.selection.isActive) {
          return;
        }

        this.$_resetTimer();

        this.shouldResetSelection = true;

        this.touchesCounter += 1;

        if (this.touchesCounter === 2) {
          this.touchesCounter = null;
          clearTimeout(this.doubleTouchTimer);
          this.doubleTouchTimer = null;
          this.shouldResetSelection = false;
          if (this.scroll) {
            await this.$_stopScrollAndAudio();
          }
          this.selection.startSelection(event);
          this.selection.addHighlightByEvent(event);
          return;
        }

        this.doubleTouchTimer = setTimeout(() => {
          this.$store.dispatch('ReadingSettingsStore/clearSelection', 0);
          this.touchesCounter = null;
        }, doubleTouchDelayByPointerType[pointerType]);

        this.$_emitSelectionEvent(SelectionEvents.START_SELECTION);

        this.longtouchTimer = setTimeout(async () => {
          this.longtouchTimer = null;
          this.shouldResetSelection = false;
          if (this.scroll) {
            await this.$_stopScrollAndAudio();
          }
          this.selection.startSelection(event);
          this.selection.addHighlightByEvent(event);
        }, longTouchDelayByPointerType[pointerType]);
      } catch (error) {
        if (error !== CustomErrorEnum.UNSELECTABLE_ELEMENTS) {
          this.$_errorHandler(error);
        }
      }
    },
    $_touchEndEventListener() {
      this.$_touchEndEventHandler();
    },
    async $_touchEndEventHandler() {
      try {
        const completedSelection = this.selection.complete();
        await this.$_lastScrollEventProcess();

        this.$_resetTimer();
        let selectionInfo = null;
        if (this.shouldResetSelection) {
          const element = this.$_getSelectionElement();

          this.selection.resetSelection(element);
          this.shouldResetSelection = false;
          selectionInfo = {};
        } else {
          selectionInfo = this.$_checkIfNotEmptySelection(completedSelection);
        }
        this.$_emitSelectionEvent(SelectionEvents.END_SELECTION, selectionInfo);
      } catch (error) {
        this.$_errorHandler(error);
      }
    },
    $_touchMoveEventListener(event) {
      this.$_touchMoveEventHandler(event);
    },
    async $_touchMoveEventHandler(event) {
      try {
        this.$_resetTimer();
        this.shouldResetSelection = false;

        await this.$_edgeAnimationScroll();

        if (!this.animateScroll) {
          this.$_processSelection(event);
        }
      } catch (error) {
        this.$_errorHandler(error);
      }
    },
    $_processSelection(event) {
      this.selection.addHighlightByEvent(event);
      if (this.selection.fullSelected) {
        return;
      }
      this.$_emitSelectionEvent(
        SelectionEvents.PROCESS_SELECTION,
        this.selection
      );
    },
    $_isClickedOnIgnoredClass(event) {
      if (!event || !event.target) {
        return true;
      }

      let isClickedOnIgnoredClass = this.ignoredClassesForSelection.some(
        ignoredClass => {
          return event.target.closest(`.${ignoredClass}`);
        }
      );
      return isClickedOnIgnoredClass;
    },
    $_mouseDownSelection(event) {
      if (!preventTextClickSelection) {
        if (this.$_isClickedOnIgnoredClass(event)) {
          return false;
        }
        this.selection.setStartPosition(event);
        this.$_emitSelectionEvent(SelectionEvents.START_SELECTION);
        if (this.selection.isActive) {
          return;
        }
        this.$store.dispatch('ReadingSettingsStore/clearSelection');
        this.debStartSelection(event);
      } else {
        this.$store.dispatch('ReadingSettingsStore/clearSelection');
      }
    },
    $_mouseDownHandler(event) {
      try {
        clearTimeout(textSelectionTimer);
        textSelectionTimer = setTimeout(() => {
          this.$_mouseDownSelection(event);
          preventTextClickSelection = false;
        }, DOUBLE_CLICK_DELAY);
      } catch (err) {
        if (err !== CustomErrorEnum.UNSELECTABLE_ELEMENTS) {
          this.$_errorHandler(err);
        }
      }
    },
    async $_performAnimationAndResetPosition(animationDirection = null) {
      await this.$_animateScrollProcess(animationDirection);

      setTimeout(() => {
        this.$_stopScrollAnimationIfNeed();
        this.selection.resetCurrentPositionOutside();
      }, this.animateScrollTime);
    },
    $_getScrollDirectionUsingTopOutside(topPosition) {
      return this.selection.isTopOutside(topPosition)
        ? SelectionDirectionEnum.TOP
        : SelectionDirectionEnum.BOTTOM;
    },
    async $_topAnimateScrollHandler() {
      const startBorderRect = this.selection?.startBorder?.getBoundingClientRect();
      if (!startBorderRect || !startBorderRect.height) {
        return;
      }
      const scrollDirection = this.$_getScrollDirectionUsingTopOutside(
        startBorderRect.top
      );
      await this.$_topAnimateScroll(scrollDirection);
    },
    async $_bottomAnimateScrollHandler() {
      const endBorderRect = this.selection.endBorder.getBoundingClientRect();
      if (!endBorderRect || !endBorderRect.height) {
        return;
      }
      const scrollDirection = this.$_getScrollDirectionUsingTopOutside(
        endBorderRect.top
      );
      await this.$_bottomAnimateScroll(scrollDirection);
    },
    async $_bottomAnimateScroll(scrollDirection) {
      if (this.selection.isEndBorderOutside()) {
        await this.$_performAnimationAndResetPosition(scrollDirection);
        return this.$_bottomAnimateScroll(scrollDirection);
      }
    },
    async $_topAnimateScroll(scrollDirection) {
      if (this.selection.isStartBorderOutside()) {
        await this.$_performAnimationAndResetPosition(scrollDirection);
        return this.$_topAnimateScroll(scrollDirection);
      }
    },
    async $_lastScrollEventProcess() {
      if (this.selection?.decoratedLocator && !this.selection.fullSelected) {
        if (this.selection.direction === SelectionDirectionEnum.BOTTOM) {
          await this.$_bottomAnimateScrollHandler();
        } else if (this.selection.direction === SelectionDirectionEnum.TOP) {
          await this.$_topAnimateScrollHandler();
        }
        this.$_stopScrollAnimationIfNeed();
      }
    },
    async $_mouseUpHandler(event) {
      try {
        await this.$_lastScrollEventProcess();
        const completedSelection = this.selection.complete();

        const selectionInfo = this.selection.clickedOutsidePara
          ? {}
          : completedSelection;
        if (!this.selection.isActive && !this.selection.clickedOutsidePara) {
          this.$store.dispatch('WikiStore/cancelAllRequests');
          this.$_emitSelectionEvent(
            SelectionEvents.END_SELECTION,
            selectionInfo
          );
          return;
        }
        // not LMB and not RMB respectively
        if (event.button !== 0 && event.button !== 2) {
          return;
        }
        // RMB
        if (event.button === 2) {
          this.selection.addHighlightByEvent(event);
        }
        if (
          selectionInfo.locator &&
          isEqual(
            selectionInfo.locator.startLocator,
            selectionInfo.locator.endLocator
          )
        ) {
          return;
        }
        this.$_emitSelectionEvent(SelectionEvents.END_SELECTION, selectionInfo);
      } catch (err) {
        this.$_errorHandler(err);
      }
    },
    async $_stopScrollAndAudio() {
      this.$store.dispatch('PlaybackStore/pause');
      this.scroll.stop();
      this.$store.commit('PublicationStore/setAnimateScroll', null);
    },
    async $_mouseMoveHandler(event) {
      try {
        this.selection.processSelection(event);

        if (event.button === 0 && event.buttons > 0) {
          if (this.scroll) {
            await this.$_stopScrollAndAudio();
          }
          if (this.selection?.decoratedLocator) {
            await this.$_edgeAnimationScroll();
          }

          this.$_processSelection(event);
        } else if (this.selection.isActive) {
          this.selection.complete();
        }
      } catch (err) {
        if (err !== CustomErrorEnum.UNSELECTABLE_ELEMENTS) {
          this.$_errorHandler(err);
        }
      }
    },
    $_wheelHandler(e) {
      if (this.animateScroll) {
        e?.preventDefault();
      }
    },
    getScrollListenerElement() {
      return this.$store.getters['PublicationStore/getScrollElementSelector'];
    },

    async $_edgeAnimationScroll() {
      const isCurrentPositionOutside =
        this.selection.currentPositionOutside?.topOutside ||
        this.selection.currentPositionOutside?.bottomOutside;

      if (!this.animateScroll && isCurrentPositionOutside) {
        await this.$_performAnimationAndResetPosition();
        return this.$_edgeAnimationScroll();
      }
    },
    getAnimationOffset(animationDirection) {
      const bookScroll = this.$store.getters[
        'PublicationStore/getScrollElement'
      ];
      if (!bookScroll?.scrollTop && bookScroll?.scrollTop !== 0) {
        return null;
      }

      let offset = bookScroll.scrollTop;
      switch (animationDirection) {
        case SelectionDirectionEnum.TOP: {
          offset -= 200;
          break;
        }
        case SelectionDirectionEnum.BOTTOM: {
          offset += 200;
          break;
        }
        default: {
          offset = this.selection.currentPositionOutside.topOutside
            ? offset - 200
            : offset + 200;
          break;
        }
      }
      return offset;
    },
    async $_animateScrollProcess(animationDirection) {
      const offset = this.getAnimationOffset(animationDirection);
      if (!offset) {
        return;
      }
      const animationElementSelector = this.getScrollListenerElement();
      const { promise, animateScroll } = scrollUtils.animatedScroll(
        animationElementSelector,
        offset,
        this.animateScrollTime
      );
      this.animateScroll = animateScroll;
      return promise;
    },
    $_stopScrollAnimationIfNeed() {
      if (this.animateScroll) {
        this.animateScroll.stop();
        this.animateScroll = null;
      }
    },
    async $_dbClickSelection(event) {
      const hasSelectedRange = this.selection.decoratedLocator !== null;
      if (
        this.selection.isActive ||
        hasSelectedRange ||
        this.$_isClickedOnIgnoredClass(event)
      ) {
        return;
      }
      this.selection.startSelection(event);
      this.selection.addHighlightByEvent(event);
      const selectionInfo = this.selection.complete();
      if (
        isEqual(
          selectionInfo.locator.startLocator,
          selectionInfo.locator.endLocator
        )
      ) {
        return;
      }
      if (this.scroll) {
        await this.$_stopScrollAndAudio();
      }
      this.$_emitSelectionEvent(SelectionEvents.END_SELECTION, selectionInfo);
    },
    $_dbClickHandler(event) {
      try {
        clearTimeout(textSelectionTimer);
        clearTimeout(startSelectionTimer);
        preventTextClickSelection = true;
        this.$_dbClickSelection(event);
      } catch (err) {
        if (err !== CustomErrorEnum.UNSELECTABLE_ELEMENTS) {
          this.$_errorHandler(err);
        }
      }
    },
    $_checkIfNotEmptySelection(selectionInfo) {
      const isEmptySelection =
        selectionInfo.locator &&
        selectionInfo.locator.startLocator.equals(
          selectionInfo.locator.endLocator
        );
      return isEmptySelection ? {} : selectionInfo;
    },
    $_scrollHandler() {
      try {
        const selectedElement = window.document.querySelector(
          `.${this.selection.notaClassName}`
        );
        if (!selectedElement) {
          this.selection.undecorate();
          return;
        }
        if (this.selection.decoratedLocator) {
          this.selection.repaintSelectionBorder();
        }
      } catch (err) {
        this.$_errorHandler(err);
      }
    },
    $_errorHandler(error) {
      logger.error(`Error in selection: ${error}`);
      this.selection.undecorate();
      this.$_emitSelectionEvent(SelectionEvents.ERROR, error);
    },
    $_emitSelectionEvent(type, data) {
      if (type === SelectionEvents.END_SELECTION && !data?.locator) {
        return;
      }
      this.$emit('SelectionEvent', { type, data });
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import 'TextSelection.less';
</style>
